/**
 * Sidecar streams — the non-DOM data that rides a session replay.
 *
 * A DOM recording alone answers "what did the screen look like". These answer
 * the questions a developer actually asks next:
 *
 * | stream        | tag             | what it carries                               |
 * |---------------|-----------------|-----------------------------------------------|
 * | page context  | `cpt-page`      | which EMR + which page/note type (metadata)   |
 * | messages      | `cpt-message`   | the panel <-> EMR-page injector protocol      |
 * | api calls     | `cpt-api`       | backend path, status, timing, and body sizes   |
 * | EMR DOM       | `cpt-emr`       | rrweb events from the EMR page itself          |
 *
 * They are emitted as rrweb custom events, so they ride the existing chunk pipe
 * with no backend change and land in timeline order — the viewer can line a
 * failing API call up against the exact frame where it happened.
 *
 * ============================ PHI, read this ============================
 *
 * Message, EMR DOM, and authenticated JMPost body streams are recorded VERBATIM
 * and unmasked, including the patient chart and session credentials.
 *
 * The controls are therefore not masking, they are:
 *   1. WHETHER a replay runs at all — decided by the server, per account, in
 *      /v1/replay/policy (see ./index.js). No client-side switch.
 *   2. WHO can watch one — the viewer is dev-only and 2FA-gated, and every
 *      endpoint behind it re-checks server-side.
 *   3. Retention — a single TTL, see REPLAY_RETENTION_DAYS.
 *
 * The product already signs a BAA with every user at signup and already ships
 * screen *video* of the EMR to Slack through the existing bug reporter, so this
 * is not a new category of exposure — it is the same exposure, indexed and
 * searchable, which is what makes it useful.
 * ========================================================================
 */

import recorder from './recorder';
import { SIDECAR_TAGS } from './sidecarStreams';

export { SIDECAR_TAGS } from './sidecarStreams';

/**
 * Emit, swallowing everything.
 *
 * These emitters are called from the hot path of every API call and every
 * injector message, so a diagnostic stream must be incapable of turning a
 * working feature into a broken one. recorder.addCustom already catches
 * internally; this is the second layer, and it is the one that holds if the
 * recorder module is missing, half-initialised, or refactored later.
 */
function emit(tag, payload) {
  try {
    return recorder.addCustom(tag, payload);
  } catch (e) {
    return false;
  }
}

const MAX_BODY_CHARS = 20000;

function clip(value) {
  if (typeof value !== 'string') return value;
  return value.length > MAX_BODY_CHARS
    ? `${value.slice(0, MAX_BODY_CHARS)}…[truncated ${value.length} chars]`
    : value;
}

/**
 * Page/EMR context. ALWAYS emitted — metadata only, no chart content.
 * `url` is reduced to origin + path: query strings are where EMRs put patient
 * and appointment ids.
 */
export function notePageContext({ emr, url, noteType, surface } = {}) {
  let safeUrl = null;
  try {
    if (url) {
      // Injectors report page URLs both absolute and bare ("app.webpt.com/...").
      const absolute = /^[a-z]+:\/\//i.test(url) ? url : `https://${url}`;
      const parsed = new URL(absolute);
      safeUrl = `${parsed.origin}${parsed.pathname}`;
    }
  } catch (e) {
    safeUrl = null;
  }
  return emit(SIDECAR_TAGS.page, {
    kind: 'page',
    emr: emr || null,
    url: safeUrl,
    noteType: noteType || null,
    surface: surface || null,
  });
}

/**
 * One message on the panel <-> EMR-page protocol (GET_DOM, PUSH_ANSWER, …).
 */
export function noteMessage({ channel, dir, type, args, response, error } = {}) {
  return emit(SIDECAR_TAGS.message, {
    kind: 'message',
    channel: channel || 'tabs',
    dir: dir || null,
    type: type || null,
    args: safeJson(args),
    response: safeJson(response),
    error: error || null,
  });
}

/** One of our own backend calls. JMPost includes full request/response bodies. */
export function noteApiCall({
  url,
  method,
  status,
  error,
  durationMs,
  requestBytes,
  responseBytes,
  request,
  response,
} = {}) {
  if (!recorder.isRecording) return false;
  return emit(SIDECAR_TAGS.api, {
    kind: 'api',
    url: url || null,
    method: method || 'POST',
    status: status ?? null,
    durationMs: durationMs ?? null,
    requestBytes: requestBytes ?? null,
    responseBytes: responseBytes ?? null,
    error: error ? clip(String(error)) : null,
    ...(request === undefined
      ? {}
      : { request: safeJson(request, false), response: safeJson(response, false) }),
  });
}

/**
 * One console line, from either context.
 *
 * `source` is what makes the merged console readable: 'cx' for the Comprehend
 * app and 'injector' for injector diagnostics forwarded from the EMR page.
 * Keeping them in one stream rather than two is deliberate — cross-context
 * causality is the thing a developer is chasing.
 */
export function noteConsole({ source, level, message, stack } = {}) {
  return emit(SIDECAR_TAGS.console, {
    kind: 'console',
    source: source || 'cx',
    level: level || 'log',
    message: clip(String(message ?? '')),
    stack: stack ? clip(String(stack)) : null,
  });
}

/**
 * A batch of rrweb events recorded on the EMR page itself, forwarded from the
 * content script. This is the patient chart, recorded unmasked — see the header.
 *
 * Batched rather than one custom event per rrweb event — an EMR page produces
 * thousands, and one custom event each would bloat the timeline.
 */
export function noteEmrDomBatch(events, meta) {
  if (!Array.isArray(events) || events.length === 0) return false;
  return emit(SIDECAR_TAGS.emr, {
    kind: 'emr-dom',
    meta: meta || null,
    events,
  });
}

/**
 * Deep-clone through JSON so a live object (an axios response, a DOM node on a
 * message payload) cannot be mutated after we buffer it, and so anything
 * unserialisable is dropped here rather than at upload time.
 *
 * Circular references are replaced individually rather than failing the whole
 * value.
 */
function safeJson(value, shouldClipStrings = true) {
  if (value === undefined || value === null) return null;
  try {
    const ancestors = [];
    return JSON.parse(
      JSON.stringify(value, function (key, val) {
        if (typeof val === 'string') return shouldClipStrings ? clip(val) : val;
        if (typeof val === 'function') return '[function]';
        if (val && typeof val === 'object') {
          while (ancestors.length && ancestors[ancestors.length - 1] !== this) {
            ancestors.pop();
          }
          if (ancestors.includes(val)) return '[circular]';
          ancestors.push(val);
        }
        return val;
      }),
    );
  } catch (e) {
    // BigInt, a throwing getter, a Proxy that rejects reads…
    try {
      return { __unserializable: String(value).slice(0, 200) };
    } catch (e2) {
      return { __unserializable: true };
    }
  }
}
